import { memo, useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";

import { StatementsList } from "components/organisms";
import { PageHeader } from "components/molecules";

import {
	ComplexFilterItem,
	ComplexFilterType,
	DateComplexFilter,
	DateRangeFilter,
	DateFilterType,
} from "interfaces/filters";
import { getPropertiesList } from "state/modules/properties";
import {
	MultiSelect,
	Badge,
	Button,
	useIsMobile,
	DateRangePicker,
	DateRange,
} from "@hotelise/ui";

import {
	getStatements,
	getStatementsFilter,
	getStatementsCount,
	getStatementsList,
	getStatementsLoading,
	getStatementsTotal,
	setStatementsFilter,
} from "state/modules/statements";

const DATE_PRESETS = [
	{
		label: "Last 30 days",
		getRange: () => ({
			from: dayjs().subtract(30, "day").startOf("day").toISOString(),
			to: dayjs().endOf("day").toISOString(),
		}),
	},
	{
		label: "Last month",
		getRange: () => ({
			from: dayjs().subtract(1, "month").startOf("month").toISOString(),
			to: dayjs().subtract(1, "month").endOf("month").toISOString(),
		}),
	},
	{
		label: "YTD",
		getRange: () => ({
			from: dayjs().startOf("year").toISOString(),
			to: dayjs().endOf("day").toISOString(),
		}),
	},
	{
		label: "Last year",
		getRange: () => ({
			from: dayjs().subtract(1, "year").startOf("year").toISOString(),
			to: dayjs().subtract(1, "year").endOf("year").toISOString(),
		}),
	},
];

const StatementsPage = () => {
	const filters = useSelector(getStatementsFilter);
	const propertiesList = useSelector(getPropertiesList);
	const statements = useSelector(getStatementsList);
	const isStatementsLoading = useSelector(getStatementsLoading);
	const statementsCount = useSelector(getStatementsCount);
	const statementsTotal = useSelector(getStatementsTotal);

	const properties = useMemo(
		() => propertiesList
			.filter((p) => p.id)
			.map((p) => ({ id: p.id, label: p.name || "-" })),
		[propertiesList]
	);
	const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>(["all"]);
	const [activePreset, setActivePreset] = useState<string | null>(null);

	const dispatch = useDispatch();

	const hasMore = statementsCount < statementsTotal;
	const loadMore = () => {
		if (!isStatementsLoading) {
			dispatch(getStatements({ isLoadMore: true }));
		}
	};

	useEffect(() => {
		dispatch(getStatements({ isLoadMore: false }));
	}, []);

	const dateFilter = useMemo(() => {
		const f = filters.find(
			(filter) => filter.type === ComplexFilterType.DATE
		) as DateComplexFilter | undefined;
		return f?.value;
	}, [filters]);

	const dateRange: DateRange = useMemo(() => {
		if (!dateFilter?.dateFrom || !dateFilter?.dateTo) return {};
		return {
			from: new Date(dateFilter.dateFrom),
			to: new Date(dateFilter.dateTo),
		};
	}, [dateFilter]);

	const handleDateChange = useCallback(
		(range: DateRange) => {
			setActivePreset(null);
			const newDateValue: DateRangeFilter = {
				dateFrom: range.from ? dayjs(range.from).startOf("day").toISOString() : null,
				dateTo: range.to ? dayjs(range.to).endOf("day").toISOString() : null,
				dateFromType: DateFilterType.CHECKOUT_TIME,
				dateToType: DateFilterType.CHECKOUT_TIME,
			};

			const newDateFilter: ComplexFilterItem = {
				type: ComplexFilterType.DATE,
				value: newDateValue,
			};

			const hasDateFilter = filters.some(
				(f) => f.type === ComplexFilterType.DATE
			);
			const updatedFilters = hasDateFilter
				? filters.map((f: ComplexFilterItem) =>
						f.type === ComplexFilterType.DATE ? newDateFilter : f
					)
				: [...filters, newDateFilter];

			dispatch(setStatementsFilter(updatedFilters));
			dispatch(getStatements({}));
		},
		[dispatch, filters]
	);

	const handlePresetClick = useCallback(
		(preset: (typeof DATE_PRESETS)[number]) => {
			setActivePreset(preset.label);
			const range = preset.getRange();

			const newDateFilter: ComplexFilterItem = {
				type: ComplexFilterType.DATE,
				value: {
					dateFrom: range.from,
					dateTo: range.to,
					dateFromType: DateFilterType.CHECKOUT_TIME,
					dateToType: DateFilterType.CHECKOUT_TIME,
				} as DateRangeFilter,
			};

			const hasDateFilter = filters.some(
				(f) => f.type === ComplexFilterType.DATE
			);
			const updatedFilters = hasDateFilter
				? filters.map((f: ComplexFilterItem) =>
						f.type === ComplexFilterType.DATE ? newDateFilter : f
					)
				: [...filters, newDateFilter];

			dispatch(setStatementsFilter(updatedFilters));
			dispatch(getStatements({}));
		},
		[dispatch, filters]
	);

	const propertyOptions = useMemo(
		() => [{ id: "all", label: "All Properties" }, ...properties],
		[properties]
	);

	const handleChangeProperty = (selected: string[]) => {
		const ids = selected.includes("all") ? ["all"] : selected;
		setSelectedPropertyIds(ids);

		const propertyIdValue =
			ids.includes("all") || ids.length === 0
				? null
				: ids.join(",");

		const propertyFilter = {
			type: ComplexFilterType.PROPERTY_ID,
			value: propertyIdValue,
		} as ComplexFilterItem;

		const hasPropertyFilter = filters.some(
			(f) => f.type === ComplexFilterType.PROPERTY_ID
		);
		const updatedFilters = hasPropertyFilter
			? filters.map((filter: ComplexFilterItem) =>
					filter.type === ComplexFilterType.PROPERTY_ID
						? propertyFilter
						: filter
				)
			: [...filters, propertyFilter];

		dispatch(setStatementsFilter(updatedFilters));
		dispatch(getStatements({}));
	};

	const handleReset = useCallback(() => {
		setActivePreset(null);
		setSelectedPropertyIds(["all"]);
		dispatch(setStatementsFilter([]));
		dispatch(getStatements({}));
	}, [dispatch]);

	return (
		<div id="statementsContainer" className="w-full h-screen overflow-auto bg-background sm:pb-24 md:h-auto md:min-h-screen">
			<PageHeader
				title="Statements"
				description="Review financial statements for your properties."
			/>
			<div className="flex flex-col gap-2 p-4 bg-card rounded-lg border border-border mb-2 z-10 relative">
				{/* Date Presets */}
				<div className="flex flex-wrap gap-1.5">
					{DATE_PRESETS.map((preset) => (
						<Badge
							key={preset.label}
							variant={activePreset === preset.label ? "secondary" : "outline"}
							className="cursor-pointer hover:bg-accent transition-colors px-2 py-0.5 text-xs"
							onClick={() => handlePresetClick(preset)}
						>
							{preset.label}
						</Badge>
					))}
				</div>

				{/* Filters – mobile */}
				<div className="flex md:hidden items-center gap-2">
					<DateRangePicker
						value={dateRange}
						onChange={handleDateChange}
						numberOfMonths={1}
						className="flex-1 w-auto text-xs h-8"
					/>
					<MultiSelect
						items={propertyOptions}
						selectedItems={selectedPropertyIds}
						onSelectionChange={handleChangeProperty}
						placeholder="Property"
						className="flex-1"
						renderSelectedTags={false}
					/>
					<Button variant="ghost" size="sm" className="shrink-0" onClick={handleReset}>
						Reset
					</Button>
				</div>

				{/* Filters – desktop */}
				<div className="hidden md:flex flex-wrap items-start gap-4">
					<div className="space-y-1">
						<span className="text-xs font-medium text-muted-foreground">Date range</span>
						<div>
							<DateRangePicker
								value={dateRange}
								onChange={handleDateChange}
								className="w-[260px]"
							/>
						</div>
					</div>

					<div className="space-y-1">
						<span className="text-xs font-medium text-muted-foreground">Property</span>
						<MultiSelect
							items={propertyOptions}
							selectedItems={selectedPropertyIds}
							onSelectionChange={handleChangeProperty}
							placeholder="Select properties..."
							className="w-[260px]"
							renderSelectedTags={false}
						/>
						{selectedPropertyIds[0] !== "all" && (
							<div className="flex flex-wrap gap-1 mt-1">
								{selectedPropertyIds
									.filter((id) => id !== "all")
									.map((id) => {
										const label =
											propertyOptions.find((p) => p.id === id)?.label || id;
										return (
											<Badge key={id} variant="secondary" className="text-xs">
												{label}
												<button
													type="button"
													aria-label={`Remove ${label}`}
													className="ml-1 leading-none hover:opacity-80"
													onClick={() => {
														const next = selectedPropertyIds.filter(
															(s) => s !== id
														);
														handleChangeProperty(
															next.length === 0 ? ["all"] : next
														);
													}}
												>
													&times;
												</button>
											</Badge>
										);
									})}
							</div>
						)}
					</div>

					<div className="space-y-1">
						<span className="text-xs font-medium text-muted-foreground">&nbsp;</span>
						<div className="flex items-center h-9">
							<Button variant="ghost" size="sm" onClick={handleReset}>
								Reset
							</Button>
						</div>
					</div>
				</div>
			</div>
			<StatementsList
				statements={statements}
				isLoading={isStatementsLoading}
				hasMore={hasMore}
				loadMore={loadMore}
				showProperty
				scrollableTarget="statementsContainer"
			/>
		</div>
	);
};

export default memo(StatementsPage);
